export interface RevisionUploadContext {
  role?: string | null;
  projectStatus?: string | null;
  isArchived?: boolean;
  // Omitted when the project has no revisions yet.
  latestRevisionStatus?: string | null;
}

const normalize = (value?: string | null): string =>
  (value ?? "").toLowerCase().replace(/[-\s]/g, "_");

// Mirrors the project card's upload rules, which the card hides on the detail page.
export const canUploadRevision = ({
  role,
  projectStatus,
  isArchived,
  latestRevisionStatus,
}: RevisionUploadContext): boolean => {
  if (isArchived) return false;
  const roleKey = normalize(role);
  if (roleKey !== "designer" && roleKey !== "both") return false;
  const statusKey = normalize(projectStatus);
  if (!statusKey.includes("preliminary") && !statusKey.includes("final_review")) {
    return false;
  }
  return normalize(latestRevisionStatus) !== "waiting_for_client_response";
};

// A revision in review needs "Start Working" before the upload page will accept files.
export const needsStartWorking = (latestRevisionStatus?: string | null): boolean =>
  normalize(latestRevisionStatus) === "in_review";

export interface StartWorkingContext {
  role?: string | null;
  isArchived?: boolean;
  latestRevisionStatus?: string | null;
}

// The designer can pick up a revision the client has sent back for changes.
export const canStartWorking = ({
  role,
  isArchived,
  latestRevisionStatus,
}: StartWorkingContext): boolean => {
  if (isArchived) return false;
  const roleKey = normalize(role);
  if (roleKey !== "designer" && roleKey !== "both") return false;
  return needsStartWorking(latestRevisionStatus);
};

export type StartWorkingState = "hidden" | "enabled" | "disabled";

// Hidden when the control never applies; disabled once work is under way, so
// clicking it leaves a visible result instead of the button vanishing.
export const startWorkingState = ({
  role,
  isArchived,
  latestRevisionStatus,
}: StartWorkingContext): StartWorkingState => {
  if (isArchived) return "hidden";
  const roleKey = normalize(role);
  if (roleKey !== "designer" && roleKey !== "both") return "hidden";
  if (needsStartWorking(latestRevisionStatus)) return "enabled";
  if (normalize(latestRevisionStatus) === "in_progress") return "disabled";
  return "hidden";
};
